<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <div
    id="audience_logic_details"
    class="flex-v-full">
    <div
      v-if="isAudienceLogicLoaded"
      class="flex-v-full">
      <breadcrumbs :items="breadcrumbs" />
      <div class="main-content-header">
        <h1 id="audienceLogics_details_header">
          <div class="icon-label-and-actions">
            <div class="icon-and-label">
              <icon-colored-audienceLogic />
              <span data-test-name="audienceName">{{ audienceLogic.name }}</span>
            </div>
            <entity-actions
              :actions="actions"
              @changed="dispatchAction" />
          </div>
          <div class="kpis">
            <div
              v-if="audienceLogic.state === AudienceLogicState.Unsubmitted"
              class="kpi">
              <div class="value">
                <span class="badge badge-danger">
                  {{ capitalize(audienceLogic.stateDescription) }}
                </span>
              </div>
              <div class="label">{{ $t('names.status', { n: 1 }) }}</div>
            </div>
            <div
              v-if="audienceLogic.state === AudienceLogicState.Submitted"
              class="kpi">
              <div class="value">
                <span class="badge badge-info">
                  {{ capitalize(audienceLogic.stateDescription) }}
                </span>
              </div>
              <div class="label">{{ $t('names.status', { n: 1 }) }}</div>
            </div>
            <div
              v-if="audienceLogic.state === AudienceLogicState.Completed"
              class="kpi">
              <div class="value">
                <span class="badge badge-success">
                  {{ capitalize(audienceLogic.stateDescription) }}
                </span>
              </div>
              <div class="label">{{ $t('names.status', { n: 1 }) }}</div>
            </div>
            <div class="kpi">
              <div class="value">
                {{ audienceLogic.modifiedOnFormatted }}
              </div>
              <div class="label">{{ $t('messages.lastModifiedOn') }}</div>
            </div>
          </div>
        </h1>
      </div>

      <div class="container-fluid details-container">
        <div
          v-if="!isCuratorOrganization && audienceLogic.state === AudienceLogicState.Unsubmitted"
          class="row">
          <div class="col-12">
            <div
              class="alert alert-danger"
              style="margin-bottom: 1.6rem">
              <icon-warning />
              <div>{{ $t('messages.audienceLogicNotYetSubmittedDescription') }}</div>
            </div>
          </div>
        </div>

        <!-- SUMMARY -->
        <audience-logic-summary
          v-show="audienceLogic.audienceRules.length > 0"
          :audience-logic="audienceLogic" />

        <!-- DETAILS -->
        <div
          v-show="expressionTree.nodes.length > 0"
          class="row">
          <div class="col-12">
            <div class="pane-header">
              <span class="icon"><icon-colored-audienceLogic /></span>
              <span>{{ capitalize($t('names.detail', { n: 2 })) }}</span>
            </div>
          </div>
          <div class="col-12">
            <div class="pane-content">
              <div class="row">
                <div class="col-12">
                  <audience-logic-node
                    v-if="areGeofenceGroupsReady"
                    ref="audienceLogicNode"
                    :node="expressionTree"
                    :geofence-groups="geofenceGroups"
                    :is-editable="false"
                    :type="AudienceLogicTypes.Group"
                    :selected-data-provider="selectedDataProvider" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-show="expressionTree.nodes.length === 0"
          class="row">
          <div class="empty-state">
            <icon-complex-rule />
            <div>
              <div class="header">{{ $t('messages.audienceLogicExpressionEmptyStateTitle') }}</div>
              <div class="details">
                <span>{{ $t('messages.audienceLogicExpressionEmptyStateDescription') }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- LINE ITEMS -->
        <template v-if="!isCuratorOrganization">
          <div class="pane-header">
            <span class="icon"><icon-colored-line-item /></span>
            <span class="text">{{ capitalize($t('names.lineItem', { n: 2 })) }}</span>
          </div>
          <div class="row">
            <div
              id="line_items_list_table_container"
              class="col-12">
              <line-items-table
                id="audiencelogic_details_lineitems_list_table"
                ref="lineItemsTable"
                :filters-changed-callback="onLineItemsTableFiltersChanged"
                :is-loading-line-items="isLoadingLineItems"
                :line-items-total-count="lineItemsTotalCount"
                :line-items="lineItems"
                :show-filters="true" />
            </div>
          </div>
        </template>
      </div>
    </div>

    <modal
      :visible="isCannotEditModalVisible"
      :title="capitalize($t('messages.cannotEditAudienceLogic'))"
      :body="capitalize($t('messages.cannotEditAudienceLogicDescription'))"
      :button1-caption="capitalize($t('actions.yes'))"
      :button2-visible="false"
      @confirmed="isCannotEditModalVisible = false" />
  </div>
</template>

<script type="text/babel">
  import antlr4 from 'antlr4';
  import { capitalize, computed, markRaw } from 'vue';
  import { mapActions, mapGetters } from 'vuex';

  import GoogleAnalyticsApi from '@/common/apis/GoogleAnalyticsApi';
  import TableColumn from '@/common/components/controls/table/TableColumn';
  import LineItemsTable from '@/common/components/lineitems/LineItemsTable';
  import { AudienceLogicTypes } from '@/common/enums/audience-logic-types';
  import { AudienceRuleModes, isExternalSegmentRule } from '@/common/enums/audience-rule-modes';
  import AudienceLogicState from '@/common/enums/AudienceLogicState.enum';
  import { isLifesightDataProvider } from '@/common/enums/data-provider-codes';
  import {
    IconColoredAudienceLogic,
    IconColoredDataPoints,
    IconColoredLineItem,
    IconColoredMobilePhone,
  } from '@/common/icons/colored';
  import { IconComplexRule } from '@/common/icons/complex';
  import { IconEdit, IconWarning } from '@/common/icons/monochrome';
  import { AudienceLogic, Filter } from '@/common/models';
  import AudienceGroup from '@/common/models/audience/AudienceGroup';
  import AudienceRule from '@/common/models/audience/AudienceRule';
  import FilterCollection from '@/common/models/query-params/FilterCollection';
  import * as FilterComparisonOperator from '@/common/models/query-params/FilterComparisonOperator';
  import QueryParams from '@/common/models/query-params/QueryParams';
  import AudienceLogicSummary from '@/org-app/components/audienceLogics/AudienceLogicSummary.vue';
  import { useDataProviders } from '@/org-app/services/tanstack/queries';
  import { waitForPendingQueries } from '@/org-app/services/tanstack/queryUtils';
  import { AudienceLogicLexer } from '@/utils/antlr4/AudienceLogicLexer';
  import { AudienceLogicListener } from '@/utils/antlr4/AudienceLogicListener';
  import { AudienceLogicParser } from '@/utils/antlr4/AudienceLogicParser';
  import AudienceSegmentUtils from '@/utils/AudienceSegmentUtils';
  import i18n from '@/vueI18n';

  import AudienceLogicNode from './AudienceLogicNode';

  export default {
    components: {
      AudienceLogicSummary,
      AudienceLogicNode,
      IconColoredAudienceLogic,
      IconColoredLineItem,
      IconComplexRule,
      IconWarning,
      LineItemsTable,
    },
    data() {
      return {
        audienceLogic: {},
        breadcrumbs: [
          {
            name: capitalize(i18n.global.t('names.audienceLogic', { n: 2 })),
            url: '/audiences/',
          },
        ],
        expressionTree: [],
        externalDemographics: [],
        filters: [], // Filters are initialized in mounted()
        geofenceGroups: [],
        groupedExternalDemographics: [],
        isAudienceLogicLoaded: false,
        isCannotEditModalVisible: false,
        isLoadingLineItems: false,
        areGeofenceGroupsReady: false,
        lineItems: [],
        lineItemsTotalCount: 0,
        totalCount: 0,
        IconColoredMobilePhone,
        IconColoredDataPoints,
        dataProvidersQuery: useDataProviders(),
      };
    },
    provide() {
      return {
        audienceLogic: computed(() => this.audienceLogic),
      };
    },
    computed: {
      AudienceLogicTypes() {
        return AudienceLogicTypes;
      },
      AudienceLogicState() {
        return AudienceLogicState;
      },
      ...mapGetters(['permissions']),
      ...mapGetters('orgApp', ['isDemandOrganization', 'enableAudienceGeoStatistics', 'isCuratorOrganization']),
      actions() {
        return [
          {
            type: 'edit',
            name: capitalize(i18n.global.t('actions.edit')),
            icon: markRaw(IconEdit),
            $isDisabled:
              this.permissions == null ||
              !this.permissions.canWriteAudiencelogic ||
              this.audienceLogic?.hasMastercardRule,
          },
        ];
      },
      columns() {
        const columns = {
          link: TableColumn.fromObject({
            label: capitalize(i18n.global.t('names.name', { n: 1 })),
            property: 'name',
          }),
        };
        return columns;
      },
      geofences() {
        return this.geofenceGroups == null ? [] : this.geofenceGroups.reduce((m, x) => [...m, ...x.geofences], []);
      },
      selectedDataProvider() {
        if (this.audienceLogic.dataProviderID) {
          return this.dataProviders.find((dp) => dp.id === this.audienceLogic.dataProviderID);
        }
        return this.dataProviders.find(isLifesightDataProvider);
      },
      dataProviders() {
        return this.dataProvidersQuery.items;
      },
    },
    watch: {
      audienceLogic(val) {
        if (val !== null) {
          const chars = new antlr4.InputStream(val.logicalExpression);
          const lexer = new AudienceLogicLexer(chars);
          const tokens = new antlr4.CommonTokenStream(lexer);
          const parser = new AudienceLogicParser(tokens);
          const tree = new AudienceGroup();
          const listener = new AudienceLogicListener(val.audienceRules, tree);
          parser.addParseListener(listener);
          parser.parse();
          this.expressionTree = tree;
        }
      },
    },
    //
    // Lifecycle hooks
    //
    async mounted() {
      await waitForPendingQueries([this.dataProvidersQuery]);

      const audienceLogic = await this.getAudienceLogic({ id: this.$route.params.id });
      // TODO remove the line below when https://hivestack.atlassian.net/browse/TOM-1650 is done
      const keysInLogicalExpression = audienceLogic.logicalExpression.split(/[(,)&|!]+/);
      const audienceRulesInLogicalExpression = audienceLogic.audienceRules.filter(
        (rule) => keysInLogicalExpression.indexOf(rule.key.toString()) > -1,
      );
      const newRules = [];
      let logicalExpression = '';

      for await (const rule of audienceRulesInLogicalExpression) {
        // PredefinedDemographics rules should be combined in one when displaying in the frontend
        if (rule.mode === AudienceRuleModes.PredefinedDemographic) {
          const group = await this.getAudienceRuleCustomGroup({ id: rule.customGroupID });
          const alreadyExistsInNewRules = newRules.find(
            (r) => r.mode === AudienceRuleModes.PredefinedDemographic && r.name === rule.name,
          );
          const externalId = AudienceSegmentUtils.getSegmentIDFromCustomGroup(group, this.selectedDataProvider);
          const queryParams = new QueryParams(
            1,
            0,
            new FilterCollection([new Filter('external_id', [externalId], FilterComparisonOperator.EQUAL)]),
          );
          queryParams.customQueryParams.dataProviderId = this.dataProviders.find(isLifesightDataProvider).id;
          const res = await queryParams.run(this.getLifesightAudienceSegmentsByOrg);
          const lifesightSegment = res.items[0];

          if (alreadyExistsInNewRules) {
            alreadyExistsInNewRules.segments.push(lifesightSegment);
          } else {
            newRules.push(
              AudienceRule.fromObject({
                ...rule,
                segments: [lifesightSegment],
              }),
            );
          }
        } else if (isExternalSegmentRule(rule.mode)) {
          const group = await this.getAudienceRuleCustomGroup({ id: rule.customGroupID });
          const externalId = AudienceSegmentUtils.getSegmentIDFromCustomGroup(group, this.selectedDataProvider);
          const queryParams = new QueryParams(
            1,
            0,
            new FilterCollection([new Filter('external_id', [externalId], FilterComparisonOperator.EQUAL)]),
          );
          const { dataProviderID } = this.audienceLogic;
          if (dataProviderID) {
            queryParams.customQueryParams.dataProviderId = dataProviderID;
            const [res] = await queryParams.run(this.getExternalSegmentsByOrg).then((r) => r.items);
            rule.segments = [res];
          }
          newRules.push(rule);
        } else if (rule.customGroupID) {
          rule.audienceRuleCustomGroup = await this.getAudienceRuleCustomGroup({
            id: rule.customGroupID,
          });
          newRules.push(rule);
        } else {
          newRules.push(rule);
        }
      }

      if (newRules.filter((rule) => rule.mode === AudienceRuleModes.PredefinedDemographic).length > 0) {
        // rewrite the logical expression now that predefined demographics rules have been smashed together
        logicalExpression = AudienceSegmentUtils.rewriteLogicalExpression(audienceLogic, newRules);
      } else {
        logicalExpression = audienceLogic.logicalExpression;
      }

      window.document.title = `${window.document.title}: ${audienceLogic.name}`;
      this.audienceLogic = AudienceLogic.fromObject({
        ...audienceLogic,
        audienceRules: newRules,
        logicalExpression,
      });
      this.breadcrumbs.push({ name: audienceLogic.name });
      this.isAudienceLogicLoaded = true;
      await this.fetchGeofenceGroups();
      if (
        this.audienceLogic.externalDemographicIDs !== undefined &&
        this.audienceLogic.externalDemographicIDs.length > 0
      ) {
        const queryParams = QueryParams.for({
          top: 100,
          skip: 0,
          filterCollection: new FilterCollection([
            new Filter(
              'externaldemographic_id',
              this.audienceLogic.externalDemographicIDs,
              FilterComparisonOperator.IN,
            ),
          ]),
          sort: this.sort,
          expand: this.expand,
        });
        this.getExternalDemographics(queryParams).then(
          (externalDemographics) => {
            this.externalDemographics = externalDemographics;
            const groups = this.externalDemographics.reduce((mem, x) => {
              const mutatedMem = { ...mem };
              if (mutatedMem[x.dataProviderID]) {
                mutatedMem[x.dataProviderID].push(x);
              } else {
                mutatedMem[x.dataProviderID] = [x];
              }
              return mutatedMem;
            }, {});
            this.groupedExternalDemographics = Object.keys(groups).map((x) => {
              return {
                dataProvider: this.dataProviders.find((y) => y.id === parseInt(x, 10)),
                externalDemographics: groups[x],
              };
            });
          },
          () => {
            // Do nothing
          },
        );
      }

      this.reloadLineItems();
    },
    methods: {
      ...mapActions(['updateAudienceLogicAudienceRules']),
      ...mapActions('orgApp', [
        'getAllDataProviders',
        'getAudienceLogic',
        'getExternalDemographics',
        'getGeofenceGroups',
        'getLineItems',
        'getLineItemDemographicTargets',
        'getAudienceRuleCustomGroup',
        'getLifesightAudienceSegmentsByOrg',
        'getExternalSegmentsByOrg',
        'getAllDataProviders',
      ]),
      capitalize(text) {
        const fn = capitalize;
        return fn(text);
      },
      dispatchAction(action) {
        const value = `${this.audienceLogic.id} - ${this.audienceLogic.name}`;

        GoogleAnalyticsApi.sendEvent('AudienceLogics', {
          category: capitalize(action.type),
          value,
        });
        switch (action.type) {
          case 'edit':
            this.edit();
            break;
          default:
            break;
        }
      },
      edit() {
        this.$router.push(`/audiences/${this.audienceLogic.id}/edit`).catch(() => {
          // Do nothing
        });
      },
      async fetchGeofenceGroups() {
        const geofenceGroupIDs = this.audienceLogic.audienceRules
          .filter((x) => x.geofenceGroupID != null)
          .map((x) => x.geofenceGroupID)
          .reduce((m, x) => (m.indexOf(x) === -1 ? [...m, x] : m), []); // unique
        if (geofenceGroupIDs.length === 0) {
          this.areGeofenceGroupsReady = true;
          return;
        }

        try {
          const geofenceGroups = await this.getGeofenceGroups({
            queryParams: QueryParams.for({
              expand: ['geofences'],
              filterCollection: new FilterCollection([
                new Filter('geofencegroup_id', geofenceGroupIDs, FilterComparisonOperator.IN),
              ]),
            }),
          });
          this.geofenceGroups = geofenceGroups.items;
        } finally {
          this.areGeofenceGroupsReady = true;
        }
      },
      reloadLineItems() {
        if (this.$refs.lineItemsTable == null || this.$refs.lineItemsTable.getReferenceToTable() == null) {
          setTimeout(this.reloadLineItems, 100);
          return;
        }
        const { queryParams } = this.$refs.lineItemsTable.getReferenceToTable();
        const demographicFilters = queryParams.filterCollection.filters.filter(
          (x) => x.property === 'lineitem_demographictargets.demographic_id',
        );
        if (demographicFilters.length === 0) {
          queryParams.filterCollection.filters.push(
            new Filter('lineitem_demographictargets.demographic_id', [this.audienceLogic.demographicID]),
          );
        }
        this.fetchLineItems(queryParams);
      },
      async fetchLineItems(queryParams) {
        try {
          this.isLoadingLineItems = true;
          const response = await queryParams.run(this.getLineItems);
          if (response) {
            this.lineItems = response.items;
            this.lineItemsTotalCount = response.totalCount;
          }
        } finally {
          this.isLoadingLineItems = false;
        }
      },
      onTableFiltersChanged(queryParams) {
        queryParams.parentID = this.audienceLogic.id;
        this.fetchLineItems(queryParams);
      },
      onLineItemsTableFiltersChanged(queryParams) {
        const demographicFilters = queryParams.filterCollection.filters.filter(
          (x) => x.property === 'lineitem_demographictargets.demographic_id',
        );
        if (demographicFilters.length === 0) {
          queryParams.filterCollection.filters.push(
            new Filter('lineitem_demographictargets.demographic_id', [this.audienceLogic.demographicID]),
          );
        }
        this.fetchLineItems(queryParams);
      },
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss">
  @use '@/styles/variables' as *;

  #app {
    #audience_logic_details {
      .well.formula {
        flex-grow: 1;
        font-family: $monospace-font-family;
        transition: box-shadow 0.2s ease-in-out;
        padding-bottom: 0;
        border-radius: 0.8rem;

        background: var(--info);
        color: var(--infoReverse);

        .rule {
          background-color: rgba(255, 255, 255, 0.2);
          // background-color: rgba(0, 0, 0, 0.05);
          padding: 0.6rem 1.2rem;
          border-radius: 0.6rem;
          margin-bottom: 1.2rem;
          display: inline-block;
        }
      }

      .data-providers {
        padding: 0;
        margin-left: 1.6rem;
        list-style: none;

        img {
          height: 2rem;
          margin-bottom: 1rem;
        }
      }

      .alert {
        display: flex;
        align-items: center;

        svg {
          width: 3.2rem;
          height: 3.2rem;
          margin-right: 1.6rem;
        }
      }
    }

    .debug-backend {
      padding-top: 1rem;
      border-top: 1px solid;
      margin-top: 1rem;
      display: flex;
      flex-direction: column;

      border-top: 0.1rem solid var(--defaultButtonBorderColor);
    }
  }
</style>
